import { LinearGradient } from "expo-linear-gradient";
import type { ReactNode } from "react";
import { StyleSheet, View } from "react-native";
import { Text } from "react-native-paper";

import { colors, fonts, gradients } from "@/lib/theme";

interface Props {
    /** Two-digit chapter number, rendered as the 160px ghost numeral. */
    num: string;
    /** Display title — appended with a trailing period (e.g. "Questions."). */
    title: string;
    /** Italic helper line below the title. */
    helper: string;
    children?: ReactNode;
    padTop?: number;
    padBottom?: number;
}

/**
 * Flat-section chapter wrapper: ghost numeral watermark + gradient-rail
 * chapter tag + bold display title + italic helper.
 *
 * The numeral sits inside a right-aligned wrapper that takes up 60% of the
 * section width — this guarantees RN cannot let the Text element overflow
 * past the section edge regardless of font-metric quirks. (The HTML demo's
 * `right: 0` on a bare Text was relying on browser font metrics to fit the
 * 200px glyph; on iOS the actual rendered width was ~10-15% wider and the
 * final digit clipped at the screen edge.)
 */
export function Chapter({ num, title, helper, children, padTop = 44, padBottom = 36 }: Props) {
    return (
        <View style={[styles.section, { paddingTop: padTop, paddingBottom: padBottom }]}>
            <View style={styles.ghostWrap} pointerEvents="none">
                <Text style={styles.ghostNumeral} numberOfLines={1} adjustsFontSizeToFit>
                    {num}
                </Text>
            </View>
            <View style={styles.header}>
                <View style={styles.railRow}>
                    <LinearGradient
                        colors={[...gradients.brand]}
                        start={{ x: 0, y: 0 }}
                        end={{ x: 1, y: 1 }}
                        style={styles.rail}
                    />
                    <Text style={styles.chapterLabel}>Chapter {num}</Text>
                </View>
                <Text style={styles.title}>{title}.</Text>
                <Text style={styles.helper}>{helper}</Text>
            </View>
            <View style={styles.body}>{children}</View>
        </View>
    );
}

const styles = StyleSheet.create({
    section: {
        position: "relative",
        paddingHorizontal: 22,
        // Clip anything that escapes — last-line defense against font-metric
        // overflow on real devices (curved screen corners on Pro Max etc.).
        overflow: "hidden"
    },
    ghostWrap: {
        position: "absolute",
        top: 12,
        right: 22,
        // 60% of section width — bounds the Text element so it can't extend
        // past the section's right inner edge. Right-aligns its child.
        width: "60%",
        alignItems: "flex-end"
    },
    ghostNumeral: {
        fontFamily: fonts.extraBold,
        fontSize: 160,
        letterSpacing: -4,
        lineHeight: 144,
        color: "rgba(11,11,31,0.05)",
        includeFontPadding: false,
        textAlign: "right"
    },
    header: {
        position: "relative",
        marginBottom: 24
    },
    railRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: 8,
        marginBottom: 6
    },
    rail: {
        width: 28,
        height: 3,
        borderRadius: 1.5
    },
    chapterLabel: {
        fontFamily: fonts.extraBold,
        fontSize: 11,
        color: colors.teal600,
        letterSpacing: 1.4,
        textTransform: "uppercase"
    },
    title: {
        fontFamily: fonts.extraBold,
        fontSize: 42,
        letterSpacing: -1.3,
        lineHeight: 42,
        color: colors.ink,
        marginBottom: 6
    },
    helper: {
        fontFamily: fonts.medium,
        fontSize: 13.5,
        color: colors.textSecondary,
        fontStyle: "italic"
    },
    body: {
        position: "relative"
    }
});
